// src/components/SubscriptionGuard.js
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth } from '../firebase/config/config';
import { Loader2, Lock } from 'lucide-react';
import logger from '../utils/logger';
import '../pages/Auth/GetStarted.css';
import { useUser } from '../context/UserContext';

/**
 * SubscriptionGuard component
 * Protects routes that require an active subscription
 * Redirects users without subscriptions to /get-started
 */
const SubscriptionGuard = ({ children }) => {
  const navigate = useNavigate();
  const { signOutUser } = useUser();
  const [loading, setLoading] = useState(true);
  const [hasSubscription, setHasSubscription] = useState(false);

  const checkSubscription = useCallback(async () => {
    // Bypass subscription check on staging domain
    const isStaging = window.location.hostname === 'staging.smushlabs.ai';
    if (isStaging) {
      logger.log('🚀 Staging mode: bypassing subscription check');
      setHasSubscription(true);
      setLoading(false);
      return;
    }

    try {
      const user = auth.currentUser;

      if (!user) {
        // Not authenticated, redirect to login
        navigate('/login');
        return;
      }

      const token = await user.getIdToken();
      const response = await fetch('/api/subscription/status', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to check subscription');
      }

      const data = await response.json();

      if (data.subscription && data.subscription.status === 'active') {
        setHasSubscription(true);
        setLoading(false);
      } else {
        // No active subscription, redirect to get-started
        setLoading(false);
        setHasSubscription(false);
      }
    } catch (error) {
      logger.error('Error checking subscription:', error);
      setLoading(false);
      setHasSubscription(false);
    }
  }, [navigate]);

  useEffect(() => {
    checkSubscription();
  }, [checkSubscription]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-purple-600 mx-auto mb-4" />
          <p className="text-gray-600">Checking subscription status...</p>
        </div>
      </div>
    );
  }

  if (!hasSubscription) {
    return (
      <div className="get-started-page" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div className="embossed-text">SMUSHLABS</div>
        <div className="logo-container">
          <img src="/favicon.ico" alt="SmushLabs Logo" className="logo" />
        </div>

        <div style={{
          background: 'rgba(255, 255, 255, 0.8)',
          backdropFilter: 'blur(12px)',
          border: '1px solid rgba(255, 255, 255, 0.6)',
          borderRadius: '35px',
          padding: '3rem',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03)',
          maxWidth: '600px',
          width: '100%',
          textAlign: 'center',
          position: 'relative',
          zIndex: 10
        }}>
          <div className="mb-6 flex justify-center">
            <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center">
              <Lock className="w-10 h-10 text-gray-900" />
            </div>
          </div>

          <h1 className="text-3xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Segoe UI, sans-serif' }}>
            Subscription Required
          </h1>

          <p className="text-gray-600 mb-8" style={{ fontSize: '16px', lineHeight: '1.6' }}>
            You need an active subscription to access the dashboard and platform features.
            Please choose a plan to continue.
          </p>

          <div className="space-y-4">
            <button
              onClick={() => navigate('/get-started')}
              style={{
                background: 'rgb(0, 0, 0)',
                color: '#ffffff',
                borderRadius: '1000px',
                padding: '1rem 1.5rem',
                fontWeight: 600,
                width: '100%',
                border: 'none',
                transition: 'all 0.2s ease',
                fontSize: '16px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              onMouseOver={(e) => e.currentTarget.style.background = 'rgb(23, 23, 23)'}
              onMouseOut={(e) => e.currentTarget.style.background = 'rgb(0, 0, 0)'}
            >
              Choose a Plan
            </button>

            <button
              onClick={async () => {
                await signOutUser();
                navigate('/');
              }}
              style={{
                background: 'transparent',
                color: '#4b5563',
                borderRadius: '1000px',
                padding: '0.75rem 1.5rem',
                fontWeight: 500,
                width: '100%',
                border: '1px solid #e5e7eb',
                transition: 'all 0.2s ease',
                fontSize: '14px',
                cursor: 'pointer'
              }}
              onMouseOver={(e) => e.currentTarget.style.background = '#f9fafb'}
              onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
            >
              Sign Out
            </button>
          </div>

          <div className="mt-8 pt-6 border-t border-gray-200">
            <p className="text-sm text-gray-600 mb-4">
              Need help? Contact our support team
            </p>
            <button
              onClick={() => navigate('/legal/contact-us')}
              className="text-sm text-gray-500 hover:text-gray-900 underline"
              style={{ background: 'none', border: 'none', cursor: 'pointer' }}
            >
              Contact Support
            </button>
          </div>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

export default SubscriptionGuard;
